import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import Link from 'next/link';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import WideContainer, { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { EVENTS } from '@r1-frontend/shared/constants/analytics';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import { useAppContext } from '~/src/features/_layout/App/AppContext';
import PhoneInfo from '~/src/features/_layout/Header/commonComponents/PhoneInfo';
import { ICommonHeaderBaseProps } from '~/src/features/_layout/Header/types';
import { getCurrentCity } from '~/src/store/selectors/city';

import MainMenuItem from '../MainMenuItem';
import PartnersMenuItem from '../PartnersMenuItem';
import ServiceMenuItem from '../ServiceMenuItem';

import * as ST from './styled';

export interface IProps extends ICommonHeaderBaseProps {
    ClientActions?: JSX.Element,
    children?: JSX.Element,
}

const CommonDesktopHeader = ({
    items,

    hasPhoneInfo = true,
    ClientActions,
    children,
}: IProps): JSX.Element => {
    const city = useSelector(getCurrentCity);
    const { isScrollingUp } = useAppContext();
    // атрибут data-visible для автотестирования
    const isDesktop = useMatchMediaByWidth(wideBreakpoints.laptop, true);

    const { partners, service, main } = items;

    const onDataLayerSendEventHandler = useCallback((menuType: 'Main' | 'Right', title: string) => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: `${menuType} menu click`,
            action: title,
        });
    }, []);

    return (
        <ST.Wrapper data-test='main-menu-desktop' data-visible={isDesktop ? 'displayed' : 'none'}>
            <ST.TopWrapperOffset isScrollingUp={!!isScrollingUp}>
                <ST.TopWrapper>
                    <WideContainer>
                        <ST.CitySelectionBlock/>

                        {partners.length
                            ? <ST.PartnersBlock>
                                {partners.map(partner => (
                                    <PartnersMenuItem
                                        key={partner.id}
                                        partner={partner}
                                        onDataLayerHandler={onDataLayerSendEventHandler}
                                        data-test='partner-item'
                                    />
                                ))}
                            </ST.PartnersBlock>
                            : null
                        }

                        {service.length
                            ? <ST.ServiceBlock>
                                {service.map(serviceItem => (
                                    <ServiceMenuItem
                                        key={serviceItem.id}
                                        service={serviceItem}
                                        onDataLayerHandler={onDataLayerSendEventHandler}
                                        data-test='service-item'
                                    />
                                ))}
                            </ST.ServiceBlock>
                            : null
                        }

                        {hasPhoneInfo ? <PhoneInfo/> : null}
                    </WideContainer>
                </ST.TopWrapper>
            </ST.TopWrapperOffset>
            <ST.MainWrapperOffset>
                <ST.MainWrapper>
                    <ST.Container>

                        <ST.LogoBlock>
                            <Link href='/' passHref>
                                <ST.SMenuLogo href='/' cityData={city}/>
                            </Link>
                        </ST.LogoBlock>

                        {main.length
                            ? <ST.MainBlockInnerContainer>
                                {main.map(mainItem => (
                                    <MainMenuItem
                                        key={mainItem.id}
                                        mainMenuItem={mainItem}
                                        onDataLayerHandler={onDataLayerSendEventHandler}
                                        data-test='main-item'
                                    />
                                ))}
                            </ST.MainBlockInnerContainer>
                            : null
                        }

                        {ClientActions}

                        {children}
                    </ST.Container>
                </ST.MainWrapper>

            </ST.MainWrapperOffset>

        </ST.Wrapper>);
};

export default CommonDesktopHeader;
